/**
 * 発注ワークフロー API サービス
 *
 * faro-api の `/api/purchase-orders` 系 6 エンドポイントを呼び出すクライアント関数群。
 * レスポンスの ISO 日付文字列は `purchaseOrderMapper.ts` で `Date` に変換する。
 */

import { apiClient } from '@/shared/lib/axios';
import { logger } from '@/shared/lib/logger';
import { authService } from '@/features/auth/services/authService';
import type {
  PurchaseOrderViewModel,
  PurchaseOrderListResult,
  PurchaseOrderListQuery,
  CreatePurchaseOrderRequest,
  ConfirmArrivalRequest,
  ConfirmArrivalResult,
  ReverseArrivalRequest,
  ReverseArrivalResult,
  CancelPurchaseOrderRequest,
} from '../types/purchaseOrder';
import {
  mapApiPurchaseOrderToViewModel,
  mapApiListResultToViewModel,
  mapApiConfirmArrivalResult,
  mapApiReverseArrivalResult,
  type ApiPurchaseOrderViewModel,
  type ApiPurchaseOrderListResult,
  type ApiConfirmArrivalResult,
  type ApiReverseArrivalResult,
} from '../utils/purchaseOrderMapper';

// ========== レスポンス共通エンベロープ ==========

interface ApiSuccessResponse<T> {
  readonly success: true;
  readonly data: T;
}

interface ApiErrorBody {
  readonly success: false;
  readonly error: { readonly code: string; readonly message: string };
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorBody;

/**
 * 共通エラー文言("予期しないエラー" のフォールバック)
 *
 * Axios エラーから取り出した API エラーの code/message が使えないときに使用。
 */
const FALLBACK_ERROR_MESSAGE = 'リクエストの処理に失敗しました';

/**
 * 成功レスポンスから data を取り出すヘルパ
 *
 * faro-api 共通の `{ success: true, data }` エンベロープを剥がす。
 * `success: false` のレスポンスはバックエンドが 4xx/5xx で返すため
 * 通常は Axios の interceptor が throw する経路。万が一 200 で `success: false`
 * が返った場合に備えて Error を投げる。
 */
function unwrapApiResponse<T>(response: { readonly data: ApiResponse<T> }): T {
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.error.message || FALLBACK_ERROR_MESSAGE);
}

// ========== API 関数 ==========

/**
 * 発注登録(POST /api/purchase-orders)
 */
export async function createPurchaseOrder(params: {
  readonly request: CreatePurchaseOrderRequest;
}): Promise<PurchaseOrderViewModel> {
  try {
    const csrfToken = await authService.getCsrfToken();
    const response = await apiClient.post<ApiResponse<ApiPurchaseOrderViewModel>>(
      '/api/purchase-orders',
      params.request,
      { headers: { 'x-csrf-token': csrfToken } }
    );
    const apiData = unwrapApiResponse(response);
    return mapApiPurchaseOrderToViewModel({ api: apiData });
  } catch (error) {
    logger.captureError(error, 'purchaseOrderApiService.createPurchaseOrder');
    throw error;
  }
}

/**
 * 発注一覧取得(GET /api/purchase-orders)
 */
export async function getPurchaseOrders(params: {
  readonly query: PurchaseOrderListQuery;
}): Promise<PurchaseOrderListResult> {
  try {
    const response = await apiClient.get<ApiResponse<ApiPurchaseOrderListResult>>(
      '/api/purchase-orders',
      { params: params.query }
    );
    const apiData = unwrapApiResponse(response);
    return mapApiListResultToViewModel({ api: apiData });
  } catch (error) {
    logger.captureError(error, 'purchaseOrderApiService.getPurchaseOrders');
    throw error;
  }
}

/**
 * 発注詳細取得(GET /api/purchase-orders/:id)
 */
export async function getPurchaseOrderById(params: {
  readonly purchaseOrderId: number;
}): Promise<PurchaseOrderViewModel> {
  try {
    const response = await apiClient.get<ApiResponse<ApiPurchaseOrderViewModel>>(
      `/api/purchase-orders/${params.purchaseOrderId}`
    );
    const apiData = unwrapApiResponse(response);
    return mapApiPurchaseOrderToViewModel({ api: apiData });
  } catch (error) {
    logger.captureError(
      error,
      `purchaseOrderApiService.getPurchaseOrderById(${params.purchaseOrderId})`
    );
    throw error;
  }
}

/**
 * 着荷確認(POST /api/purchase-orders/:id/arrivals)
 */
export async function confirmArrival(params: {
  readonly purchaseOrderId: number;
  readonly request: ConfirmArrivalRequest;
}): Promise<ConfirmArrivalResult> {
  try {
    const csrfToken = await authService.getCsrfToken();
    const response = await apiClient.post<ApiResponse<ApiConfirmArrivalResult>>(
      `/api/purchase-orders/${params.purchaseOrderId}/arrivals`,
      params.request,
      { headers: { 'x-csrf-token': csrfToken } }
    );
    const apiData = unwrapApiResponse(response);
    return mapApiConfirmArrivalResult({ api: apiData });
  } catch (error) {
    logger.captureError(
      error,
      `purchaseOrderApiService.confirmArrival(${params.purchaseOrderId})`
    );
    throw error;
  }
}

/**
 * 着荷取消(巻き戻し、POST /api/purchase-orders/:id/arrival-reversals)
 */
export async function reverseArrival(params: {
  readonly purchaseOrderId: number;
  readonly request: ReverseArrivalRequest;
}): Promise<ReverseArrivalResult> {
  try {
    const csrfToken = await authService.getCsrfToken();
    const response = await apiClient.post<ApiResponse<ApiReverseArrivalResult>>(
      `/api/purchase-orders/${params.purchaseOrderId}/arrival-reversals`,
      params.request,
      { headers: { 'x-csrf-token': csrfToken } }
    );
    const apiData = unwrapApiResponse(response);
    return mapApiReverseArrivalResult({ api: apiData });
  } catch (error) {
    logger.captureError(
      error,
      `purchaseOrderApiService.reverseArrival(${params.purchaseOrderId})`
    );
    throw error;
  }
}

/**
 * 発注キャンセル(POST /api/purchase-orders/:id/cancel)
 */
export async function cancelPurchaseOrder(params: {
  readonly purchaseOrderId: number;
  readonly request: CancelPurchaseOrderRequest;
}): Promise<PurchaseOrderViewModel> {
  try {
    const csrfToken = await authService.getCsrfToken();
    const response = await apiClient.post<ApiResponse<ApiPurchaseOrderViewModel>>(
      `/api/purchase-orders/${params.purchaseOrderId}/cancel`,
      params.request,
      { headers: { 'x-csrf-token': csrfToken } }
    );
    const apiData = unwrapApiResponse(response);
    return mapApiPurchaseOrderToViewModel({ api: apiData });
  } catch (error) {
    logger.captureError(
      error,
      `purchaseOrderApiService.cancelPurchaseOrder(${params.purchaseOrderId})`
    );
    throw error;
  }
}
